/**
 * Buscador global — la pieza central de la interfaz.
 *
 * POR QUE ES UN OVERLAY Y NO UNA RUTA
 * -----------------------------------
 * Consultar un precio es la operacion mas frecuente del dia: 30 o 40 veces,
 * casi siempre con alguien esperando del otro lado de un WhatsApp o parado en
 * el mostrador. Y casi siempre ocurre MIENTRAS se esta haciendo otra cosa.
 *
 * Si consultar fuera una ruta, ir a mirar un precio desmontaria el formulario de
 * venta a medio cargar. Ese solo hecho alcanza para que alguien vuelva a la
 * planilla de Google, donde mirar otra pestaña no cuesta nada. Por eso esto vive
 * por ENCIMA del arbol de rutas y nunca navega.
 *
 * ES UNA PALETA DE BUSQUEDA
 * -------------------------
 * Un `Dialog` de Radix (focus trap, Escape, `aria-modal`) en su variante
 * "paleta", la misma que `SelectorCliente`: anclado arriba, sin titulo visible
 * (queda `sr-only`), con el campo de busqueda como cabecera y la lista de
 * resultados como cuerpo. Centrado, la lista se saldria por abajo mientras se
 * tipea. Los atajos que lo ABREN (⌘K, "/") viven en `useAtajoBuscador`; los
 * que lo recorren (flechas, Enter) en el campo; Escape lo maneja Radix.
 *
 * QUE HACE Y QUE NO
 * -----------------
 * ENCUENTRA y PREPARA. Nunca EJECUTA plata. Escribir "cobrar 50000" no cobra
 * nada: un numero suelto no dice de que moneda es, y adivinarlo en un sistema
 * bimonetario es exactamente el error que no se puede cometer. Toda operacion de
 * dinero termina en su pantalla, con la moneda declarada y confirmacion.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Package, User, Truck, Wrench, Copy, Check, FileText, type LucideIcon } from "lucide-react";

import { cn, formatearFecha } from "@/lib/utils";
import { api, mensajeDeError } from "@/lib/api";
import { useProveedores } from "@/lib/queries";
import type { ResultadoParaTicket } from "@/lib/ticket-store";
import { Aviso } from "@/components/ui/aviso";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Cargando } from "@/components/ui/cargando";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Importe } from "@/components/money/importe";
import { MuestraColor } from "@/components/catalogo/MuestraColor";
import { nombreCompleto } from "@/components/catalogo/nombre";
import { textoAntiguedad } from "@/components/taller/logica";
import { BadgeMora } from "@/components/cobro/BadgeMora";
import {
  buscaComprobante,
  comienzosDeSeccion,
  destinoDe,
  destinoDeComprobante,
  filtrarProveedores,
  textoEnter,
  textoParaCopiar,
  type Destino,
  type TipoResultado,
} from "./logica";

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

interface Precio {
  producto_id: number;
  codigo_interno: string;
  nombre: string;
  /**
   * Desde 0055 el color dejo de vivir adentro del nombre, y desde 0073 el
   * buscador devuelve tambien la memoria y la bateria: el titulo de la fila se
   * ARMA con las tres (`catalogo/nombre.ts`), porque sin ellas dos unidades
   * del mismo modelo se leen igual.
   */
  color: string | null;
  es_usado: boolean;
  memoria_gb: number | null;
  bateria_pct: number | null;
  rubro: string | null;
  precio_ars: string;
  precio_usd: string;
  precio_moneda: "ARS" | "USD";
  stock_actual: string;
  disponibilidad: "DISPONIBLE" | "A_PEDIDO" | "SIN_STOCK";
  margen_ars: string;
  /** El flag del producto: con stock puede dejar igual un encargo (0115). */
  a_pedido?: boolean;
  /** 0144: la marca de la ficha, que casi nunca esta escrita en el nombre. */
  marca?: string | null;
  texto_whatsapp: string;
}

interface ClienteResultado {
  cliente_id: number;
  nombre: string;
  saldo: string;
  saldo_ars_hoy: string;
  moneda_anclaje: "ARS" | "USD";
  cc_modo: "FIJA" | "USD";
  estado_mora: string;
}

interface OrdenResultado {
  id: number;
  numero: string;
  equipo: string;
  imei: string | null;
  estado: string;
  cliente: string | null;
  dias_en_taller: number;
}

/** Una venta, NC o ND, tal como la trae `GET /ventas`. */
interface ComprobanteResultado {
  id: number;
  numero: string;
  tipo: string;
  estado: string;
  fecha: string;
  moneda: "ARS" | "USD";
  total: string | number;
}

interface Resultados {
  productos: Precio[];
  clientes: ClienteResultado[];
  ordenes: OrdenResultado[];
  comprobantes: ComprobanteResultado[];
}

const VACIO: Resultados = { productos: [], clientes: [], ordenes: [], comprobantes: [] };

// ─────────────────────────────────────────────────────────────────────────────
// Presentacion
// ─────────────────────────────────────────────────────────────────────────────

/** Es un chip de ESTADO del producto: va al lado del nombre, nunca sobre el precio. */
const DISPONIBILIDAD: Record<
  Precio["disponibilidad"],
  { texto: string; variant: BadgeProps["variant"] }
> = {
  DISPONIBLE: { texto: "En stock", variant: "success" },
  A_PEDIDO: { texto: "A pedido", variant: "warning" },
  SIN_STOCK: { texto: "Sin stock", variant: "destructive" },
};

/** El `id` DOM de cada opcion, para `aria-activedescendant`. */
function idDeOpcion(tipo: TipoResultado, id: number): string {
  return `buscador-${tipo}-${id}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Boton de copiar para WhatsApp
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Copiar el precio al portapapeles es el final real del flujo mas frecuente: la
 * persona lo pega en WhatsApp y contesta. Que sea un toque deliberado y no
 * automatico es a proposito — copiar sin que lo pidan pisa lo que el usuario
 * tuviera guardado.
 */
function BotonCopiar({ texto }: { texto: string }): JSX.Element {
  const [copiado, setCopiado] = useState(false);

  const copiar = useCallback(
    async (e: React.MouseEvent) => {
      // La fila de atras navega o agrega al ticket: copiar no hace ninguna de
      // las dos.
      e.preventDefault();
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(texto);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 1600);
      } catch {
        // Sin permiso de portapapeles (http sin localhost, Safari viejo) se
        // selecciona el texto para que se pueda copiar a mano. Peor que el
        // ideal, pero mucho mejor que un boton que no hace nada.
        const sel = window.getSelection();
        const rango = document.createRange();
        const nodo = (e.currentTarget as HTMLElement).closest("[data-fila]");
        if (nodo && sel) {
          rango.selectNodeContents(nodo);
          sel.removeAllRanges();
          sel.addRange(rango);
        }
      }
    },
    [texto],
  );

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={copiar}
      className={cn("shrink-0", copiado && "text-success")}
      aria-label={copiado ? "Copiado" : "Copiar para WhatsApp"}
    >
      {copiado ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      {copiado ? "Copiado" : "Copiar"}
    </Button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// La fila de resultado
// ─────────────────────────────────────────────────────────────────────────────

interface FilaResultadoProps {
  id: string;
  icono: LucideIcon;
  activo: boolean;
  titulo: ReactNode;
  /** Chips y datos secundarios, en una linea de `text-xs`. */
  subtitulo?: ReactNode;
  /** Columna de la derecha: importes, chip de estado, "Editar". */
  derecha?: ReactNode;
  /** Despues de la columna derecha: el boton de copiar. */
  accion?: ReactNode;
  /**
   * A donde lleva. Con destino la fila es un `Link` (se puede abrir en otra
   * pestaña); sin destino actua sin navegar (el producto en el mostrador).
   */
  destino: Destino | null;
  onSeleccionar: () => void;
  /** El mouse manda sobre el teclado: pasar por encima mueve el resaltado. */
  onApuntar: () => void;
}

/**
 * La unica forma de una fila del buscador. Producto, cliente, proveedor y orden
 * cambian lo que ponen en cada ranura, no la fila: antes eran cuatro copias
 * con el mismo padding, el mismo icono y la misma clase de "activo", y cada
 * ajuste habia que hacerlo cuatro veces.
 */
function FilaResultado({
  id,
  icono: Icono,
  activo,
  titulo,
  subtitulo,
  derecha,
  accion,
  destino,
  onSeleccionar,
  onApuntar,
}: FilaResultadoProps): JSX.Element {
  const clases = cn(
    "flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 py-3 transition-colors",
    activo ? "bg-accent" : "hover:bg-accent",
  );

  const contenido = (
    <>
      <Icono className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{titulo}</div>
        {subtitulo ? (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            {subtitulo}
          </div>
        ) : null}
      </div>
      {derecha ? (
        <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">{derecha}</div>
      ) : null}
      {accion}
    </>
  );

  // Las filas son opciones de un listbox (`role="option"`) recorridas con
  // flechas + Enter desde el combobox, no botones de accion: por eso no hay
  // `Button` aca y el foco nunca sale del campo.
  if (destino === null) {
    return (
      <div
        id={id}
        data-fila
        role="option"
        aria-selected={activo}
        onClick={onSeleccionar}
        onMouseEnter={onApuntar}
        className={clases}
      >
        {contenido}
      </div>
    );
  }

  return (
    <Link
      to={destino.to}
      search={destino.search}
      id={id}
      data-fila
      role="option"
      aria-selected={activo}
      onClick={onSeleccionar}
      onMouseEnter={onApuntar}
      className={clases}
    >
      {contenido}
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Buscador
// ─────────────────────────────────────────────────────────────────────────────

export interface BuscadorGlobalProps {
  abierto: boolean;
  onCerrar: () => void;
  /**
   * Agregar el producto al ticket abierto, si hay uno. Recibe el resultado
   * entero y no solo id y nombre: sin el precio, la linea entraba a $ 0,00.
   */
  onAgregarAVenta?: (producto: ResultadoParaTicket) => void;
}

export function BuscadorGlobal({
  abierto,
  onCerrar,
  onAgregarAVenta,
}: BuscadorGlobalProps): JSX.Element | null {
  const [texto, setTexto] = useState("");
  const [resultados, setResultados] = useState<Resultados>(VACIO);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [indice, setIndice] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const navegar = useNavigate();

  /** Con `onAgregarAVenta` la lupa vende; sin el, navega. Lo decide la ruta. */
  const modoVenta = Boolean(onAgregarAVenta);

  // Los proveedores salen de la lista cacheada, no de una consulta por tecla:
  // son pocos (decenas, no miles) y la lista ya viene cacheada para los
  // desplegables de compras y de productos.
  const { data: paginaProveedores } = useProveedores();
  const proveedores = useMemo(
    () => filtrarProveedores(paginaProveedores?.filas ?? [], texto),
    [paginaProveedores, texto],
  );

  // Lista plana para navegar con el teclado, en el orden en que se ve.
  const planos = useMemo(
    () => [
      ...resultados.productos.map((p) => ({ tipo: "producto" as const, dato: p })),
      ...resultados.clientes.map((c) => ({ tipo: "cliente" as const, dato: c })),
      ...proveedores.map((p) => ({ tipo: "proveedor" as const, dato: p })),
      ...resultados.ordenes.map((o) => ({ tipo: "orden" as const, dato: o })),
      ...resultados.comprobantes.map((v) => ({ tipo: "comprobante" as const, dato: v })),
    ],
    [resultados, proveedores],
  );

  /** Donde arranca cada seccion dentro de `planos`. */
  const desde = useMemo(
    () =>
      comienzosDeSeccion({
        productos: resultados.productos.length,
        clientes: resultados.clientes.length,
        proveedores: proveedores.length,
        ordenes: resultados.ordenes.length,
      }),
    [resultados, proveedores],
  );

  const hayAlgo = planos.length > 0;

  // ── Busqueda con debounce ─────────────────────────────────────────────────
  useEffect(() => {
    if (!abierto) return;

    const q = texto.trim();
    if (q.length < 2) {
      setResultados(VACIO);
      setCargando(false);
      setError(null);
      // Se cancela lo que estuviera en vuelo: sin esto, borrar el texto deja
      // llegando una respuesta vieja que repuebla la lista.
      abortRef.current?.abort();
      return;
    }

    setCargando(true);
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        // Las tres consultas van en paralelo: son independientes y con alguien
        // esperando, encadenarlas triplica el tiempo hasta la respuesta.
        const [precios, clientes, ordenes, comprobantes] = await Promise.allSettled([
          // Diez y no seis: «iphone 17 pro max» son tres memorias, y con seis
          // lugares las fundas y los templados del mismo modelo se llevaban
          // dos de las tres.
          api.get<{ resultados: Precio[] }>(`/precios?q=${encodeURIComponent(q)}&limite=10`, {
            signal: ctrl.signal,
          }),
          // `todos=1`: sin eso `/deudas` lee el recorte de alertas, y la lupa
          // solo encontraba a los que estaban en mora. Un cliente al dia —o
          // uno que debe pero todavia no vencio— no aparecia nunca.
          api.get<{ filas: ClienteResultado[] }>(
            `/deudas?buscar=${encodeURIComponent(q)}&todos=1&limite=4`,
            { signal: ctrl.signal },
          ),
          api.get<{ filas: OrdenResultado[] }>(
            `/reparaciones?buscar=${encodeURIComponent(q)}&limite=4`,
            { signal: ctrl.signal },
          ),
          // Los comprobantes, por numero: «VENTA-0000103» daba «No encontré
          // nada». Solo si hay un numero escrito (ver `buscaComprobante`).
          buscaComprobante(q)
            ? api.get<{ filas: ComprobanteResultado[] }>(
                `/ventas?q=${encodeURIComponent(q)}&limite=4`,
                { signal: ctrl.signal },
              )
            : Promise.resolve({ filas: [] as ComprobanteResultado[] }),
        ]);

        if (ctrl.signal.aborted) return;

        setResultados({
          productos: precios.status === "fulfilled" ? (precios.value?.resultados ?? []) : [],
          clientes: clientes.status === "fulfilled" ? (clientes.value?.filas ?? []) : [],
          ordenes: ordenes.status === "fulfilled" ? (ordenes.value?.filas ?? []) : [],
          comprobantes:
            comprobantes.status === "fulfilled" ? (comprobantes.value?.filas ?? []) : [],
        });
        setIndice(0);

        // Que fallen clientes u ordenes no debe tapar los precios, que es lo
        // que la persona vino a buscar. Solo se muestra error si fallo todo.
        const todoMal =
          precios.status === "rejected" &&
          clientes.status === "rejected" &&
          ordenes.status === "rejected";
        setError(todoMal ? mensajeDeError(precios.reason) : null);
      } catch (e) {
        if (!ctrl.signal.aborted) setError(mensajeDeError(e));
      } finally {
        if (!ctrl.signal.aborted) setCargando(false);
      }
    }, 220);

    return () => clearTimeout(t);
  }, [texto, abierto]);

  // ── Foco y limpieza al abrir ──────────────────────────────────────────────
  useEffect(() => {
    if (abierto) {
      // El timeout deja que el dialogo se monte antes de pedir el foco; sin el,
      // el teclado del celular a veces no sube.
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
    setTexto("");
    setResultados(VACIO);
    setError(null);
    setIndice(0);
    abortRef.current?.abort();
    return undefined;
  }, [abierto]);

  /**
   * Elegir una fila: en el mostrador, un producto va al ticket y la pantalla no
   * se mueve; todo lo demas abre su ficha. Nunca cobra ni vende: eso vive en su
   * pantalla, con la moneda declarada y confirmacion explicita.
   */
  const elegir = useCallback(
    (sel: (typeof planos)[number], navegarAMano: boolean) => {
      const id =
        sel.tipo === "producto"
          ? sel.dato.producto_id
          : sel.tipo === "cliente"
            ? sel.dato.cliente_id
            : sel.dato.id;

      const destino =
        sel.tipo === "comprobante"
          ? destinoDeComprobante(sel.dato.numero)
          : destinoDe(sel.tipo, id, modoVenta);
      if (destino === null) {
        const precio = sel.dato as Precio;
        onAgregarAVenta?.({ ...precio, nombre: nombreCompleto(precio) });
      } else if (navegarAMano) {
        // Con Enter no hay Link que navegue solo.
        void navegar({ to: destino.to, search: destino.search });
      }
      onCerrar();
    },
    [modoVenta, onAgregarAVenta, navegar, onCerrar],
  );

  // ── Teclado ───────────────────────────────────────────────────────────────
  // Flechas para moverse, Enter para elegir. Escape lo maneja Radix. Las
  // flechas dan la vuelta: la lista es corta (a lo sumo 18) y volver al
  // principio desde la ultima es mas rapido que subir de a una.
  const onTecla = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!hayAlgo) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIndice((i) => (i + 1) % planos.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIndice((i) => (i - 1 + planos.length) % planos.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const sel = planos[indice];
        if (sel) elegir(sel, true);
      }
    },
    [hayAlgo, planos, indice, elegir],
  );

  if (!abierto) return null;

  const q = texto.trim();
  const sinResultados = q.length >= 2 && !cargando && !hayAlgo && !error;
  const activo = planos[indice];
  const idActivo = activo
    ? idDeOpcion(
        activo.tipo,
        activo.tipo === "producto"
          ? activo.dato.producto_id
          : activo.tipo === "cliente"
            ? activo.dato.cliente_id
            : activo.dato.id,
      )
    : undefined;

  return (
    <Dialog
      open
      onOpenChange={(sigueAbierto) => {
        if (!sigueAbierto) onCerrar();
      }}
    >
      {/* Variante "paleta": anclada arriba, sin padding propio (la cabecera y
          el pie llevan el suyo) y con el cuerpo pegado a los bordes. En el
          celular ocupa la pantalla entera: es donde mas se usa, y una ventanita
          flotante con el teclado abierto no deja ver nada. */}
      <DialogContent
        tamano="lg"
        mobile="hoja"
        className="gap-0 p-0 sm:top-[10vh] sm:max-h-[70vh] sm:translate-y-0 sm:p-0"
        // El foco lo pone el efecto de arriba, con el retraso que necesita el
        // teclado del celular; Radix no tiene que pelearselo.
        onOpenAutoFocus={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        <DialogHeader className="border-b border-border py-1 pl-1 pr-12">
          <DialogTitle className="sr-only">Buscar</DialogTitle>
          <InputBusqueda
            ref={inputRef}
            apariencia="plano"
            etiqueta="Buscá un producto, un cliente o un equipo"
            valor={texto}
            onValorChange={setTexto}
            cargando={cargando}
            onKeyDown={onTecla}
            autoCorrect="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={hayAlgo}
            aria-controls="buscador-resultados"
            aria-activedescendant={idActivo}
          />
        </DialogHeader>

        <DialogBody className="m-0 space-y-0 p-0 sm:mx-0 sm:px-0">
          {q.length < 2 && (
            <EstadoVacio
              tamano="sm"
              titulo="Escribí al menos dos letras."
              detalle="Sirve el nombre, el código del proveedor, un cliente o un IMEI."
              className="items-center py-8 text-center"
            />
          )}

          {error && (
            <Aviso tono="destructive" compacto className="m-2">
              {error}
            </Aviso>
          )}

          {cargando && !hayAlgo && !error && (
            <Cargando tamano="inline" texto="Buscando…" className="p-4" />
          )}

          {sinResultados && (
            <EstadoVacio
              tamano="sm"
              titulo={`No encontré nada con «${q}».`}
              className="items-center py-8 text-center"
            />
          )}

          <div id="buscador-resultados" role="listbox" className="p-2">
            {resultados.productos.length > 0 && (
              <Seccion titulo="Productos">
                {resultados.productos.map((p, i) => {
                  const disp = DISPONIBILIDAD[p.disponibilidad];
                  return (
                    <FilaResultado
                      key={p.producto_id}
                      id={idDeOpcion("producto", p.producto_id)}
                      icono={Package}
                      activo={indice === i}
                      destino={destinoDe("producto", p.producto_id, modoVenta)}
                      onApuntar={() => setIndice(i)}
                      onSeleccionar={() => elegir({ tipo: "producto", dato: p }, false)}
                      titulo={nombreCompleto(p)}
                      subtitulo={
                        <>
                          <Badge variant={disp.variant}>{disp.texto}</Badge>
                          {/* El color salio del nombre y paso a ser un campo:
                              sin esto, dos unidades del mismo modelo y la
                              misma memoria se ven identicas justo donde hay
                              que elegir cual se le ofrece al cliente. */}
                          {p.color && <MuestraColor color={p.color} />}
                          {p.disponibilidad === "DISPONIBLE" && (
                            <span>{Number(p.stock_actual)} u.</span>
                          )}
                          {p.marca && <span>· {p.marca}</span>}
                          {p.rubro && <span>· {p.rubro}</span>}
                        </>
                      }
                      // El precio en las dos monedas SIEMPRE. Parte de la
                      // clientela pregunta y razona en dolares, y hacer la
                      // cuenta a mano con alguien esperando es donde se
                      // equivocan los numeros.
                      derecha={
                        <>
                          <Importe monto={Number(p.precio_ars)} moneda="ARS" />
                          <Importe monto={Number(p.precio_usd)} moneda="USD" tamano="sm" />
                        </>
                      }
                      accion={<BotonCopiar texto={textoParaCopiar(p, nombreCompleto(p))} />}
                    />
                  );
                })}
              </Seccion>
            )}

            {resultados.clientes.length > 0 && (
              <Seccion titulo="Clientes">
                {resultados.clientes.map((c, i) => (
                  <FilaCliente
                    key={c.cliente_id}
                    c={c}
                    activo={indice === desde.clientes + i}
                    onApuntar={() => setIndice(desde.clientes + i)}
                    onSeleccionar={() => elegir({ tipo: "cliente", dato: c }, false)}
                  />
                ))}
              </Seccion>
            )}

            {proveedores.length > 0 && (
              <Seccion titulo="Proveedores">
                {proveedores.map((p, i) => (
                  <FilaResultado
                    key={p.id}
                    id={idDeOpcion("proveedor", p.id)}
                    icono={Truck}
                    activo={indice === desde.proveedores + i}
                    destino={destinoDe("proveedor", p.id, modoVenta)}
                    onApuntar={() => setIndice(desde.proveedores + i)}
                    onSeleccionar={() => elegir({ tipo: "proveedor", dato: p }, false)}
                    titulo={p.nombre}
                    subtitulo={
                      <span>
                        Lista en {p.moneda_lista}
                        {p.contacto ? ` · ${p.contacto}` : ""}
                        {p.telefono ? ` · ${p.telefono}` : ""}
                      </span>
                    }
                    derecha={<span className="text-xs text-muted-foreground">Editar</span>}
                  />
                ))}
              </Seccion>
            )}

            {resultados.ordenes.length > 0 && (
              // «Órdenes» y no «Equipos en el taller»: la busqueda encuentra
              // cualquier orden, tambien las entregadas y las anuladas, y el
              // titulo decia que el equipo estaba en el local cuando ya no.
              <Seccion titulo="Órdenes de taller">
                {resultados.ordenes.map((o, i) => (
                  <FilaOrden
                    key={o.id}
                    o={o}
                    activo={indice === desde.ordenes + i}
                    onApuntar={() => setIndice(desde.ordenes + i)}
                    onSeleccionar={() => elegir({ tipo: "orden", dato: o }, false)}
                  />
                ))}
              </Seccion>
            )}

            {resultados.comprobantes.length > 0 && (
              <Seccion titulo="Comprobantes">
                {resultados.comprobantes.map((v, i) => (
                  <FilaResultado
                    key={v.id}
                    id={idDeOpcion("comprobante", v.id)}
                    icono={FileText}
                    activo={indice === desde.comprobantes + i}
                    destino={destinoDeComprobante(v.numero)}
                    onApuntar={() => setIndice(desde.comprobantes + i)}
                    onSeleccionar={() => elegir({ tipo: "comprobante", dato: v }, false)}
                    titulo={<Codigo>{v.numero}</Codigo>}
                    subtitulo={
                      <span>
                        {formatearFecha(v.fecha)}
                        {v.estado === "ANULADO" ? " · anulado" : ""}
                      </span>
                    }
                    derecha={<Importe monto={Number(v.total)} moneda={v.moneda} />}
                  />
                ))}
              </Seccion>
            )}
          </div>
        </DialogBody>

        {/* ── Ayuda de teclado, solo en escritorio ── */}
        {hayAlgo && (
          <div className="hidden items-center gap-4 border-t border-border px-4 py-2 text-xs text-muted-foreground sm:flex">
            <span>
              <Tecla>↑</Tecla> <Tecla>↓</Tecla> moverse
            </span>
            <span>
              <Tecla>Enter</Tecla> {textoEnter(modoVenta, activo?.tipo)}
            </span>
            <span>
              <Tecla>Esc</Tecla> cerrar
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Un cliente con cuenta indexada al dolar tiene DOS numeros legitimos, y el
 * que se le cobra hoy es el de pesos. Decirlo evita que alguien lea el saldo
 * anclado y cobre de menos. El numero lleva siempre el color de su moneda; que
 * deba o tenga saldo a favor lo dice el badge de al lado, no el color.
 */
function FilaCliente({
  c,
  activo,
  onApuntar,
  onSeleccionar,
}: {
  c: ClienteResultado;
  activo: boolean;
  onApuntar: () => void;
  onSeleccionar: () => void;
}): JSX.Element {
  const saldo = Number(c.saldo);
  const debe = saldo > 0.009;
  const aFavor = saldo < -0.009;

  return (
    <FilaResultado
      id={idDeOpcion("cliente", c.cliente_id)}
      icono={User}
      activo={activo}
      destino={destinoDe("cliente", c.cliente_id, false)}
      onApuntar={onApuntar}
      onSeleccionar={onSeleccionar}
      titulo={c.nombre}
      subtitulo={
        <>
          <BadgeMora estado={c.estado_mora} />
          {c.cc_modo === "USD" && <span>· cuenta en dólares</span>}
        </>
      }
      derecha={
        debe ? (
          <>
            <Importe monto={Number(c.saldo_ars_hoy)} moneda="ARS" />
            {c.cc_modo === "USD" && <Importe monto={saldo} moneda="USD" tamano="sm" />}
            <span className="text-xs text-muted-foreground">debe</span>
          </>
        ) : aFavor ? (
          <>
            <Importe monto={Math.abs(Number(c.saldo_ars_hoy))} moneda="ARS" />
            <Badge variant="success">A favor</Badge>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">Sin deuda</span>
        )
      }
    />
  );
}

function FilaOrden({
  o,
  activo,
  onApuntar,
  onSeleccionar,
}: {
  o: OrdenResultado;
  activo: boolean;
  onApuntar: () => void;
  onSeleccionar: () => void;
}): JSX.Element {
  const listo = o.estado === "LISTO";

  return (
    <FilaResultado
      id={idDeOpcion("orden", o.id)}
      icono={Wrench}
      activo={activo}
      // Sin el `ot` la fila dejaba al usuario en la lista del taller, con la
      // orden que acababa de elegir en algun lugar de la lista. La ruta ya
      // sabe abrir una orden puntual.
      destino={destinoDe("orden", o.id, false)}
      onApuntar={onApuntar}
      onSeleccionar={onSeleccionar}
      titulo={
        <>
          {o.equipo}
          <Codigo className="ml-2 text-xs font-normal text-muted-foreground">{o.numero}</Codigo>
        </>
      }
      subtitulo={
        <>
          <span>
            {o.cliente ?? "Sin cliente"} · {textoAntiguedad(o, o.dias_en_taller)}
          </span>
          {/* Se busca por IMEI: verlo en la fila confirma que es ese equipo. */}
          {o.imei && (
            <span>
              · <Codigo>{o.imei}</Codigo>
            </span>
          )}
        </>
      }
      derecha={
        <Badge variant={listo ? "success" : "secondary"}>
          {listo ? "Listo para entregar" : o.estado.replace(/_/g, " ").toLowerCase()}
        </Badge>
      }
    />
  );
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }): JSX.Element {
  return (
    <div role="group" aria-label={titulo} className="mb-1">
      <TituloSeccion nivel="etiqueta" as="span" className="px-3 pb-1 pt-3">
        {titulo}
      </TituloSeccion>
      {children}
    </div>
  );
}

function Tecla({ children }: { children: ReactNode }): JSX.Element {
  return (
    <kbd className="rounded-sm border border-border bg-muted px-1.5 py-0.5 text-xs">{children}</kbd>
  );
}

/**
 * Abre el buscador con ⌘K / Ctrl+K, o con "/" cuando no se esta escribiendo en
 * otro campo.
 *
 * El atajo no reemplaza al boton: en el celular no hay teclado fisico y ahi es
 * donde mas se consulta un precio.
 */
export function useAtajoBuscador(abrir: () => void): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const enCampo =
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" ||
          e.target.tagName === "TEXTAREA" ||
          e.target.isContentEditable);

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        abrir();
        return;
      }
      if (e.key === "/" && !enCampo) {
        e.preventDefault();
        abrir();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [abrir]);
}
